import React, { useMemo, useState, useEffect } from "react";
import { API_URL } from "./config";
import { useNavigate } from "react-router-dom";
import "./dashboard.css";
import { ArrowUpRight, ArrowDownRight } from "lucide-react";

import { Doughnut, Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";
import ReactApexChart from "react-apexcharts";

ChartJS.register(CategoryScale, LinearScale, BarElement, LineElement, PointElement, ArcElement, Tooltip, Legend, Filler);

const GEO_URL = "https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json";

const ACTIVITY_MARKERS = [
  { name: "New York",  coords: [-74.006,  40.712], color: "#3b82f6" },
  { name: "Sao Paulo", coords: [-46.639, -23.550], color: "#22c55e" },
  { name: "London",    coords: [-0.127,  51.507], color: "#facc15" },
  { name: "Dubai",     coords: [ 55.270,  25.204], color: "#ef4444" },
  { name: "Singapore", coords: [103.819,   1.352], color: "#f43f5e" },
];

// Beautiful Clean SVG Sparkline Component matching continuous curves
const SmoothSparkline = ({ accentColor }) => (
  <div style={{ height: "26px", width: "100%", marginTop: "8px", overflow: "visible" }}>
    <svg viewBox="0 0 100 30" width="100%" height="100%" preserveAspectRatio="none">
      <defs>
        <linearGradient id={`areaGrad-${accentColor.replace('#','')}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={accentColor} stopOpacity="0.15" />
          <stop offset="100%" stopColor={accentColor} stopOpacity="0.0" />
        </linearGradient>
      </defs>
      <path d="M0,8 L20,2 L40,10 L60,1 L80,4 L100,1 L100,30 L0,30 Z" fill={`url(#areaGrad-${accentColor.replace('#','')})`} />
      <polyline points="0,8 20,2 40,10 60,1 80,4 100,1" fill="none" stroke={accentColor} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx="0" cy="8" r="1.5" fill={accentColor} />
      <circle cx="20" cy="2" r="1.5" fill={accentColor} />
      <circle cx="40" cy="10" r="1.5" fill={accentColor} />
      <circle cx="60" cy="1" r="1.5" fill={accentColor} />
      <circle cx="80" cy="4" r="1.5" fill={accentColor} />
      <circle cx="100" cy="1" r="1.5" fill={accentColor} />
    </svg>
  </div>
);

const StatCard = ({ title, value, icon, accent, delta, isUp = true }) => (
  <div className="dashboard-stat-card">
    <div className="dashboard-stat-top">
      <div className="dashboard-stat-icon" style={{ backgroundColor: `${accent}12` }}>
        {icon}
      </div>
      <div className="dashboard-stat-content">
        <p>{title}</p>
        <h2>{value}</h2>
        <small className={isUp ? "stat-delta-up" : "stat-delta-down"} style={{ display: "flex", alignItems: "center", gap: "2px" }}>
          {isUp ? <ArrowUpRight size={12} /> : <ArrowDownRight size={12} />}{delta.replace("↑ ","").replace("↓ ","")}
        </small>
      </div>
    </div>
    <SmoothSparkline accentColor={accent} />
  </div>
);

// How a legacy (document_requests) row looks for each status; anything else is a plain request.
const LEGACY_STATUS_VIEWS = new Map([
  ["signed", { typeText: "✍ Sign", typeClass: "sign", badgeClass: "signed" }],
  ["opened", { typeText: "👁 Verify", typeClass: "verify", badgeClass: "valid" }],
]);
const LEGACY_DEFAULT_VIEW = { typeText: "📄 Request", typeClass: "drive", badgeClass: "stored" };

const toWorkflowRow = (r) => {
  const isApproval = r.type === "Approval";
  return {
    key: `wf-${r.id}`,
    source: "workflow",
    id: r.id,
    name: r.document,
    owner: r.requester,
    typeText: isApproval ? "✔ Approve" : "✍ Sign",
    typeClass: isApproval ? "verify" : "sign",
    label: r.status,
    badgeClass: r.requestStatus === "completed" ? "signed" : "stored",
    date: r.submittedOn,
  };
};

const toLegacyRow = (d) => {
  const view = LEGACY_STATUS_VIEWS.get(d.status) ?? LEGACY_DEFAULT_VIEW;
  return {
    key: `doc-${d.id}`,
    source: "document",
    doc: d,
    id: d.id,
    name: d.file_name || d.title,
    owner: d.sender_email,
    ...view,
    label: d.status,
    date: d.created_at,
  };
};

const workflowItems = (result) =>
  (result.status === "fulfilled" && Array.isArray(result.value) ? result.value : []);

const legacyItems = (result) =>
  (result.status === "fulfilled" ? result.value?.requests || [] : []);

// Signature requests created through Request Signatures (shown on Request Status)
// + documents sent the older way. Either one failing must not hide the other.
const loadRecentRequests = async (email) => {
  const [wf, legacy] = await Promise.allSettled([
    fetch(`${API_URL}/api/workflow/requests?user_email=${encodeURIComponent(email.toLowerCase())}`)
      .then((r) => (r.ok ? r.json() : [])),
    fetch(`${API_URL}/api/recent-requests?email=${encodeURIComponent(email)}`)
      .then((r) => r.json()),
  ]);

  return [...workflowItems(wf).map(toWorkflowRow), ...legacyItems(legacy).map(toLegacyRow)]
    .sort((x, y) => new Date(y.date || 0) - new Date(x.date || 0));
};

const Dashboard = () => {
  const navigate = useNavigate();

  const user = JSON.parse(localStorage.getItem("user")) || {
  name: "Guest",
  email: "",
};

const [recentRequests, setRecentRequests] = useState([]);

useEffect(() => {
  if (!user.email) return undefined;

  const refresh = async () => setRecentRequests(await loadRecentRequests(user.email));

  refresh();
  const interval = setInterval(refresh, 10000);
  return () => clearInterval(interval);
}, [user.email]);

// Workflow requests open their details page; older documents keep the old behaviour.
const openRow = (row) =>
  row.source === "workflow"
    ? navigate(`/request-details/${encodeURIComponent(row.id)}`)
    : openDocument(row.doc);

const openDocument = async (doc) => {
  try {

    if (doc.status === "pending") {
      await fetch(`${API_URL}/api/update-document-status`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          document_id: doc.id,
          status: "opened",
        }),
      });
    }

    navigate(`/app?docId=${doc.id}`);

  } catch (err) {
    console.error(err);
    navigate(`/app?docId=${doc.id}`);
  }
};

  const chartData = useMemo(() => ({
    labels: ["May 1", "May 8", "May 15", "May 22", "May 29"],
    datasets: [
      {
        data: [60, 48, 91, 64, 82],
        borderColor: "#2563eb",
        borderWidth: 2,
        backgroundColor: "rgba(37, 99, 235, 0.05)",
        tension: 0.4,
        pointRadius: 5,
        pointHoverRadius: 7,
        pointBackgroundColor: "#2563eb",
        pointBorderColor: "#fff",
        pointBorderWidth: 2,
        fill: true,
      },
    ],
  }), []);

  const chartOptions = {
    plugins: {
      legend: { display: false },
      tooltip: {
        enabled: true,
        backgroundColor: "#1e293b",
        titleColor: "#94a3b8",
        bodyColor: "#fff",
        padding: 10,
        cornerRadius: 8,
        displayColors: false,
        callbacks: {
          label: (ctx) => `Score: ${ctx.parsed.y}`,
        },
      },
    },
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: { grid: { display: false }, ticks: { color: "#64748b", font: { size: 8 } } },
      y: { min: 0, max: 100, ticks: { stepSize: 25, color: "#64748b", font: { size: 8 } }, grid: { color: "#f1f5f9" } },
    },
  };

  const doughnutData = useMemo(() => ({
    labels: ["Completed", "Pending", "Expired", "Declined"],
    datasets: [
      {
        data: [72, 32, 12, 12],
        backgroundColor: ["#22c55e", "#f59e0b", "#ef4444", "#94a3b8"],
        borderWidth: 0,
        borderColor: "transparent",
        cutout: "75%",
      },
    ],
  }), []);

  return (
    <div className="dashboard-page">
      {/* TOP HEADER STATUS BANNER */}
      <div className="trust-banner">
        <div className="banner-left-section">
          <div className="banner-logo-web">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
          </div>
          <div className="banner-left">
            <h6>QUANTUM TRUST STATUS</h6>
            <h2>Hybrid PQ + RSA</h2>
          </div>
        </div>

        <div className="banner-center-metrics">
          <div className="banner-score">
            <p>Trust Score</p>
            <h1>91<span>/100</span></h1>
            <small>Very Strong</small>
          </div>
          <div className="banner-posture">
            <p>Security Posture</p>
            <div className="banner-posture-status">
              <h2>Secure</h2>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="#4ade80"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/></svg>
            </div>
          </div>
        </div>

        <div className="banner-actions">
          <button>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
            May 1 - May 31, 2025
          </button>
          <button>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21.5 2v6h-6M21.34 15.57a10 10 0 1 1-.57-8.38l5.67-5.67"/></svg>
            Refresh
          </button>
          <button className="primary-btn" onClick={() => navigate("/request-signatures")}>
            + New
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M6 9l6 6 6-6"/></svg>
          </button>
        </div>
      </div>

      {/* METRIC CARD STATS GRID */}
      <div className="dashboard-stats-grid">
        <StatCard title="Documents Signed" value="1,248" accent="#8b5cf6" delta="↑ 18.5% vs Apr 1 - Apr 30" icon={<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#8b5cf6" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>} />
        <StatCard title="Verified Documents" value="1,102" accent="#3b82f6" delta="↑ 21.3% vs Apr 1 - Apr 30" icon={<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><polyline points="9 11 11 13 15 9"/></svg>} />
        <StatCard title="Firmware Signed" value="320" accent="#22c55e" delta="↑ 12.7% vs Apr 1 - Apr 30" icon={<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2"><rect x="4" y="4" width="16" height="16" rx="2"/><rect x="9" y="9" width="6" height="6"/><path d="M9 1v3M15 1v3M9 20v3M15 20v3M20 9h3M20 15h3M1 9h3M1 15h3"/></svg>} />
        <StatCard title="Active Keys" value="48" accent="#eab308" delta="↑ 5.1% vs Apr 1 - Apr 30" icon={<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#eab308" strokeWidth="2"><path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4"/></svg>} />
        <StatCard title="Tamper Alerts" value="3" accent="#ef4444" delta="↓ 25% vs Apr 1 - Apr 30" isUp={false} icon={<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>} />
      </div>

      {/* MIDDLE DASHBOARD SECTION */}
      <div className="dashboard-middle-grid">
        {/* QUANTUM TRUST SCORE CIRCLE PANEL */}
        <div className="dashboard-panel">
          <h3>Quantum Trust Score</h3>
          <div className="trust-score-wrap">
            <div className="score-circle-container">
              <ReactApexChart
                type="radialBar"
                height={160}
                series={[91]}
                options={{
                  chart: { type: "radialBar", sparkline: { enabled: true } },
                  plotOptions: {
                    radialBar: {
                      startAngle: -135,
                      endAngle: 135,
                      hollow: { size: "58%" },
                      track: { background: "#e2e8f0", strokeWidth: "100%" },
                      dataLabels: {
                        name: { show: true, offsetY: 20, fontSize: "11px", color: "#64748b", formatter: () => "/100" },
                        value: { offsetY: -10, fontSize: "28px", fontWeight: 800, color: "#0f172a", formatter: (v) => v },
                      },
                    },
                  },
                  fill: {
                    type: "gradient",
                    gradient: { shade: "dark", type: "horizontal", gradientToColors: ["#10b981"], stops: [0, 100] },
                  },
                  colors: ["#2563eb"],
                  stroke: { lineCap: "round" },
                }}
              />
            </div>
            <div className="trust-breakdown">
              <div><span>Algorithm Strength</span><b>95/100</b></div>
              <div><span>Key Security</span><b>88/100</b></div>
              <div><span>Policy Compliance</span><b>92/100</b></div>
              <div><span>Infrastructure Security</span><b>90/100</b></div>
              <div><span>Activity Anomaly</span><b>85/100</b></div>
            </div>
          </div>
          <div className="panel-footer-banner">
            <p>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#1e62ec" strokeWidth="2.5"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
              Your overall security posture is excellent.
            </p>
            <a href="#!">View recommendations →</a>
          </div>
        </div>

        {/* LINE GRAPH RISK SCORE PANEL */}
        <div className="panel">
          <div className="panel-head">
            <h3>Risk Score Trend</h3>
            <button className="chip-btn">Last 30 Days ▾</button>
          </div>
          <div className="chart-wrap">
            <Line data={chartData} options={chartOptions} />
          </div>
        </div>

        {/* GEOGRAPHIC ACTIVITY MAP PANEL */}
        {/* Heatmap */}
        <div className="panel">

  <div className="panel-head">
    <h3>Global Activity Map</h3>

    <button className="chip-btn">
      All Activities ▾
    </button>
  </div>

  <div className="heatmap">
    <img
      src="/world-map-light.png"
      alt="World Map"
      className="map-image"
    />

    <div className="hotspot hotspot-red"></div>
    <div className="hotspot hotspot-orange"></div>
    <div className="hotspot hotspot-green"></div>

    <div className="heat-legend">
      <span>Low Activity</span>
      <div className="legend-bar"></div>
      <span>High Activity</span>
    </div>
  </div>

</div>
      </div>

      {/* BOTTOM DATATABLE SECTIONS */}
      <div className="bottom-grid">
        {/* RECENT TRUST OPERATIONS GRID TABLE */}
        <div className="panel">

    <div className="panel-head">
        <h3>Recent Trust Operations</h3>
    </div>

    <table className="trust-ops-table">

        <thead>

            <tr>

                <th>Type</th>

                <th>Asset</th>

                <th>Status</th>

                <th>Owner</th>

            </tr>

        </thead>

        <tbody>

        {recentRequests.length > 0 ? (

            recentRequests.slice(0,5).map((row)=>(

                <tr
                    key={row.key}
                    className="click-row"
                    onClick={() => openRow(row)}
                    style={{cursor:"pointer"}}
                >

                    <td>
                        <div className={`type-cell-wrapper ${row.typeClass}`}>
                            {row.typeText}
                        </div>
                    </td>

                    <td>
                        <div className="asset-cell-wrapper">
                            {row.name}
                        </div>
                    </td>

                    <td>
                        <span className={`status-badge ${row.badgeClass}`}>
                            {row.label}
                        </span>
                    </td>

                    <td>
                        {row.owner}
                    </td>

                </tr>

            ))

        ) : (

            <tr>

                <td colSpan="4">

                    No recent operations

                </td>

            </tr>

        )}

        </tbody>

    </table>

</div>

        {/* DOUGHNUT SIGNATURE BREAKDOWN CHART */}
        <div className="panel">
          <h3>Signature Requests Overview</h3>
          <div className="donut-wrap">
            <div className="donut-chart">
              <Doughnut data={doughnutData} options={{ responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false } } }} />
              <div className="donut-center">
                <h2>128</h2>
                <span>Total Requests</span>
              </div>
            </div>
            <div className="donut-legend">
              <div className="donut-legend-item">
                <div className="legend-label-group"><span className="legend-dot completed" />Completed</div>
                <b>72 (56%)</b>
              </div>
              <div className="donut-legend-item">
                <div className="legend-label-group"><span className="legend-dot pending" />Pending</div>
                <b>32 (25%)</b>
              </div>
              <div className="donut-legend-item">
                <div className="legend-label-group"><span className="legend-dot expired" />Expired</div>
                <b>12 (9%)</b>
              </div>
              <div className="donut-legend-item">
                <div className="legend-label-group"><span className="legend-dot declined" />Declined</div>
                <b>12 (9%)</b>
              </div>
            </div>
          </div>
          <a className="go-link" href="#!" style={{ marginTop: "auto" }}>Go to Requests →</a>
        </div>

        {/* ASSET RISK PROFILE TABLE */}
        <div className="panel">
          <div className="panel-head">
            <h3>Top Assets by Risk</h3>
            <a href="#!">View All</a>
          </div>
          <table className="risk-table">
            <thead>
              <tr>
                <th>Asset</th>
                <th>Risk Score</th>
                <th>Risk Level</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td><div className="asset-cell-wrapper"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/></svg>Legacy_Contract_2019.pdf</div></td>
                <td>45</td>
                <td><span className="risk-tag medium">◆ Medium</span></td>
              </tr>
              <tr>
                <td><div className="asset-cell-wrapper"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/></svg>Oil_Policy_Document.docx</div></td>
                <td>50</td>
                <td><span className="risk-tag medium">◆ Medium</span></td>
              </tr>
              <tr>
                <td><div className="asset-cell-wrapper"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/></svg>Unverified_Agreement.pdf</div></td>
                <td>35</td>
                <td><span className="risk-tag low">◆ Low</span></td>
              </tr>
              <tr>
                <td><div className="asset-cell-wrapper"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/></svg>Vendor_Contract_Old.pdf</div></td>
                <td>30</td>
                <td><span className="risk-tag low">◆ Low</span></td>
              </tr>
              <tr>
                <td><div className="asset-cell-wrapper"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/></svg>Employee_Data_Old.xlsx</div></td>
                <td>25</td>
                <td><span className="risk-tag low">◆ Low</span></td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* FOOTER: INFRASTRUCTURE & SERVICES HEALTH LIST */}
      <h3 className="infra-section-title">Infrastructure & Services Health</h3>
      <div className="services-grid">
        {[
          { name: "API Gateway", icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="2.5"><circle cx="12" cy="12" r="3"/><path d="M12 2v3M12 19v3M2 12h3M19 12h3"/></svg> },
          { name: "Signature Engine", icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="2.5"><path d="M12 20h9M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"/></svg> },
          { name: "Verification Engine", icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="2.5"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><polyline points="9 11 11 13 15 9"/></svg> },
          { name: "Firmware Engine", icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="2.5"><rect x="4" y="4" width="16" height="16" rx="2"/><path d="M9 9h6v6H9z"/></svg> },
          { name: "IPFS Cluster", icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="2.5"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l-7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/></svg> },
          { name: "HSM Cluster", icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="2.5"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg> },
          { name: "Blockchain Anchor", icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="2.5"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/></svg> },
          { name: "Backup & DR", icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="2.5"><path d="M18 10h-1.26A8 8 0 1 0 9 20h9a5 5 0 0 0 0-10z"/></svg> }
        ].map((x) => (
          <div className="service-card" key={x.name}>
            <div className="service-icon-box">{x.icon}</div>
            <div className="service-details">
              <h4>{x.name}</h4>
              <div className="service-status-pill">
                <svg width="8" height="8" viewBox="0 0 24 24" fill="#16a34a"><circle cx="12" cy="12" r="10"/></svg>
                Operational
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default Dashboard;